import React from 'react';
import { Box, Button, Divider, Paper, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Computer as ComputerIcon,
  ContactMail as ContactMailIcon,
  Info as InfoIcon,
  Smartphone as SmartphoneIcon
} from '@material-ui/icons';
import Config from 'config';

export interface ApplicationDetailsProps {
  appImage?: string;
  appName: React.ReactNode;
  appType: React.ReactNode;
  appDescription?: React.ReactNode;
  sectionOneHeader?: React.ReactNode;
  sectionOneDetails?: React.ReactNode;
  sectionTwoHeader?: React.ReactNode;
  sectionTwoDetails?: React.ReactNode;
  sectionOneButtonLabel?: React.ReactNode;
  sectionTwoButtonLabel?: React.ReactNode;
  sectionOneButtonLink?: string;
  sectionTwoButtonLink?: string;
  accessButtonOneLabel?: React.ReactNode;
  accessButtonTwoLabel?: React.ReactNode;
  accessButtonOneLink?: string | null | undefined;
  accessButtonTwoLink?: string | null | undefined;
}

interface AccessButtonProps {
  className?: string;
  icon: React.ReactNode;
  label: React.ReactNode | string;
  href: string;
}

const AccessButton = ({ className, icon, label, href }: AccessButtonProps): React.ReactElement => (
  <a href={href} className={className} target="_blank" rel="noopener noreferrer">
    <Button startIcon={icon} variant="contained" color="secondary" size="small" disableElevation>
      {label}
    </Button>
  </a>
);

const ApplicationDetails: React.FC<ApplicationDetailsProps> = ({
  appImage,
  appName,
  appType,
  appDescription,
  sectionOneHeader,
  sectionOneDetails,
  sectionOneButtonLabel = 'Learn More',
  sectionTwoHeader,
  sectionTwoDetails,
  sectionTwoButtonLabel = 'Contact Us',
  sectionOneButtonLink,
  sectionTwoButtonLink,
  accessButtonOneLabel,
  accessButtonTwoLabel,
  accessButtonOneLink,
  accessButtonTwoLink
}) => {
  const classes = useStyles();

  const handleContactEmail = (): Window | null => window.open(Config.productSupportUrl, '_blank');

  return (
    <Paper className={classes.root}>
      <Box className={classes.appContainer}>
        {appImage && (
          <Box className={classes.appImageContainer}>
            <img src={appImage} alt="App" />
          </Box>
        )}
        <Box className={classes.appInfoContainer}>
          <Typography variant="h3">{appName}</Typography>
          <Typography className={classes.appType} variant="subtitle2">
            {appType}
          </Typography>
          <Typography>{appDescription}</Typography>
          <Box className={classes.appInfoButtonContainer}>
            {accessButtonOneLink && (
              <AccessButton
                className={classes.appInfoButton}
                href={accessButtonOneLink}
                icon={<ComputerIcon />}
                label={accessButtonOneLabel}
              />
            )}
            {accessButtonTwoLink && (
              <AccessButton
                className={classes.appInfoButton}
                href={accessButtonTwoLink}
                icon={<SmartphoneIcon />}
                label={accessButtonTwoLabel}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Divider className={classes.divider} />
      <Box className={classes.subsectionContainer}>
        <Box className={classes.subsection}>
          <Typography variant="subtitle1">{sectionOneHeader}</Typography>
          <Typography variant="body1">{sectionOneDetails}</Typography>
        </Box>
        {sectionOneButtonLink && (
          <Box className={classes.button}>
            <a href={sectionOneButtonLink} target="_blank" rel="noopener noreferrer">
              <Button color="primary" variant="contained" disableElevation startIcon={<InfoIcon />}>
                {sectionOneButtonLabel}
              </Button>
            </a>
          </Box>
        )}
      </Box>
      <Divider className={classes.divider} />
      <Box className={classes.subsectionContainer}>
        <Box className={classes.subsection}>
          <Typography variant="subtitle1">{sectionTwoHeader}</Typography>
          <Typography variant="body1">{sectionTwoDetails}</Typography>
        </Box>
        <Box className={classes.button}>
          <a href={sectionTwoButtonLink} target="_blank" rel="noopener noreferrer">
            <Button
              variant="outlined"
              color="secondary"
              disableElevation
              onClick={handleContactEmail}
              startIcon={<ContactMailIcon />}
            >
              {sectionTwoButtonLabel}
            </Button>
          </a>
        </Box>
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(4.5),
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing(3)}px`
  },
  appContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('md')]: { flexDirection: 'column' },
    [theme.breakpoints.up('md')]: { flexDirection: 'row' },
    gap: `${theme.spacing(2)}px`
  },
  appImageContainer: {
    height: 'auto',
    minWidth: '160px',
    flex: 1,
    [theme.breakpoints.down('sm')]: { margin: 'auto', maxWidth: 400 },
    [theme.breakpoints.up('md')]: { marginTop: theme.spacing(1), maxWidth: 300 },
    padding: 0,
    '& img': { maxWidth: '100%', maxHeight: '100%' }
  },
  appInfoContainer: {
    display: 'flex',
    flex: 2,
    flexDirection: 'column',
    gap: `${theme.spacing(1)}px`
  },
  appInfoButtonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${theme.spacing(1)}px`,
    marginTop: theme.spacing(2),
    flexBasis: 'fit-content'
  },
  appInfoButton: { flexShrink: 0 },
  appType: { color: '#696969' },
  subsectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: `${theme.spacing(2)}px`,
    rowGap: `${theme.spacing(2)}px`
  },
  subsection: { flexGrow: 1, boxSizing: 'border-box', flexBasis: 'fit-content' },
  button: { flexGrow: 1, boxSizing: 'border-box', minWidth: '200px', textAlign: 'right' },
  divider: { marginLeft: -1 * theme.spacing(4), marginRight: -1 * theme.spacing(4) }
}));

export default ApplicationDetails;
